import React, { useMemo, useState } from 'react';
import { Badge } from 'react-bootstrap';
import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  shift,
  useFloating,
} from '@floating-ui/react';

import { useClickOutside } from '../../../hooks/useClickOutside';
import Icon from '../../../components/icon/Icon';
import UserAvatar from '../../../pages/profile/UserAvatar';
import { colorLib } from '../../lib/color';
import useBreakpoint from '../../../hooks/useBreakpoint';
import { UserSettings } from '@larki/mp/lib/state';

const User = ({ user }: { user: UserSettings }) => {
  const color = colorLib.getUserColor(user.color_index);
  const colorName = colorLib.getColorName(user.color_index);

  return (
    <div className='realtime-user'>
      <UserAvatar
        profileImage={user.image}
        hasBorder={true}
        borderStyle={`3px solid ${color}`}
      />
      <Badge
        className='free text-truncate'
        style={{
          background: color,
          color: '#FFF',
        }}
        title={user.name ?? undefined}
      >
        {user.name ? user.name : colorName}
      </Badge>
    </div>
  );
};

const RealtimeUsers = ({ users }: { users: UserSettings[] }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { refs, floatingStyles } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [offset(6), flip(), shift()],
    whileElementsMounted: autoUpdate, // Automatically updates position when needed
    placement: 'bottom-end',
  });

  const bpXl = useBreakpoint(['xl']);
  const bpLg = useBreakpoint(['lg']);

  const toggleDropdown = () => setIsOpen((prev) => !prev);

  useClickOutside(
    refs.reference,
    () => setIsOpen(false),
    ['.realtime-users-dropdown-container'],
    false
  );

  const { first, rest } = useMemo(() => {
    const N = bpXl ? 2 : bpLg ? 1 : 0;
    const first = users.slice(0, N);
    const rest = users.slice(N);
    return { first, rest };
  }, [users, bpXl, bpLg]);

  return (
    <div className='RealTimeUsers flex-row align-items-center'>
      {rest.length > 0 ? (
        <div
          className='dropdown-trigger d-flex flex-row px-2 mr-2'
          ref={refs.setReference}
          onClick={toggleDropdown}
        >
          <span className='font-12'>+{rest.length} Users</span>
          <Icon icon='solid-caret-down' />
        </div>
      ) : null}

      {isOpen ? (
        <FloatingPortal>
          <div
            className='realtime-users-dropdown-container'
            ref={refs.setFloating}
            style={{
              ...floatingStyles,
            }}
          >
            <div className='realtime-users-wrapper px-0'>
              {rest.map((user) => (
                <User user={user} key={user.id} />
              ))}
            </div>
          </div>
        </FloatingPortal>
      ) : null}

      <div className='realtime-users-wrapper px-0'>
        {first.map((user) => (
          <User user={user} key={user.id} />
        ))}
      </div>
    </div>
  );
};

export default RealtimeUsers;
